import { QesColors } from "@app/shared/qes-standards/colors";
import { useIsOverflow } from "@app/shared/qes-standards/utility/useIsOverflow";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { CSSProperties, FC, useId, useMemo, useRef } from "react";

export interface BarLite {
  backgroundColor: QesColors | string;
  label: string;
  numberValue: number;
}

export interface StackedBarLiteProps {
  /**
   * Reduces the segment background color opacity to 20%. This is similar in effect 
   * to the predecessor component's "isGrayscale"
   */
  isFaded?: boolean;
  /**
   * Applies a CSS grayscale(1) filter to the entire bar
   */
  isGrayscale?: boolean;
  /**
   * The segments to be shown in the bar, in the order given.
   * Segments consist of backgroundColor, label, and numberValue. 
   * Label is used in the tooltip and legend (both optional), and 
   * numberValue is either used to calculate the percentage (if it's a count) 
   * or just rendered as the percentage (if it's a percentage). <br><br>
   * Each segment has a minimum rendered width of 2%, and the segments use our 
   * useIsOverflow hook to determine if the value should be rendered in the bar.<br><br>
   * The text color is determined by a hardcoded map in the component.
   */
  segments: BarLite[];
  /**
   * Mostly determines whether the tooltip shows the count and sum of all segments.
   */
  segmentType?: "count" | "percentage"; // default "count"
  /**
   * Displays a legend under the bar, using color swatches, labels, and numberValues.
   */
  showLegend?: boolean;
  /**
   * Displays a tooltip on hover of each segment, showing the color swatch, label, 
   * percentage, and (if segmentType is "count") the numberValue and sumOfSegmentValues.
   */
  showTooltips?: boolean;
}

const backgroundColorToTextColorMap = new Map<string, string>([
  [QesColors.QesPrimaryRed, QesColors.QesPrimaryWhite],
  [QesColors.QesTertiaryRed2, QesColors.QesPrimaryWhite],
  ["#f23957", QesColors.QesPrimaryWhite],
  [QesColors.QesTertiaryYellow2, QesColors.QesPrimaryGray8],
  [QesColors.QesPrimaryGreen, QesColors.QesPrimaryWhite],
  ["#00cd87", QesColors.QesPrimaryGray8],
  [QesColors.Blue500, QesColors.QesPrimaryWhite],
]);

const MIN_SEGMENT_WIDTH = 2;
export const MetricsStackedBarLite: FC<StackedBarLiteProps> = (props: StackedBarLiteProps) => {
  const id = useId();
  const internalSegmentType = props.segmentType ?? "count";
  const sumOfSegmentValues = useMemo(() => {
    return props.segments.reduce((result, value) => result + (value.numberValue || 0), 0);
  }, [props.segments]);

  const getTooltipText = (segment: BarLite, percentageString: string) => {
    return (
      <div className="metrics-stacked-bar-lite__tooltip">
        <p className="fw-bold">
          <span className="metrics-color-swatch" style={{ backgroundColor: segment.backgroundColor }} />
          {segment.label}
        </p>
        <dl>
          <div><dt>Percentage:</dt> <dd>{percentageString}</dd></div>
          {internalSegmentType === "count" ? (
            <>
              <div><dt>Count:</dt> <dd>{(segment.numberValue || 0).toLocaleString()}</dd></div>
              <div><dt>Total Count:</dt> <dd>{sumOfSegmentValues.toLocaleString()}</dd></div>
            </>
          ) : null}
        </dl>
      </div>
    );
  };

  const barStyle = props.isGrayscale ? { filter: "grayscale(1)" } : undefined;

  const figCaption = useMemo(() => {
    return props.segments.length > 0 && sumOfSegmentValues > 0
      ? internalSegmentType === "count"
        ? `A chart with a single stacked bar. The segments indicate ${props.segments.map((segment, index, arr) => `${index === arr.length - 1 ? " and " : " "}${(segment.numberValue || 0)} (${((segment.numberValue / sumOfSegmentValues) * 100).toFixed(1)}%) ${segment.label}${index === arr.length - 1 ? "." : ""}`)}`
        : `A chart with a single stacked bar. The segments indicate ${props.segments.map((segment, index, arr) => `${index === arr.length - 1 ? " and " : " "}${(segment.numberValue || 0)}% ${segment.label}${index === arr.length - 1 ? "." : ""}`)}`
      : "A chart with no data.";
  }, [props.segments, internalSegmentType]);

  return (
    <div className="metrics-stacked-bar-lite__container" role={props.showLegend ? "group" : undefined} aria-labelledby={props.showLegend ? `legend:${id}` : undefined}>
      <figure className="metrics-stacked-bar-lite__bar" role="img" style={barStyle}>
        {props.segments.map((segment) => {
          const percentageNumber = internalSegmentType === "count" ? (((segment.numberValue || 0) / sumOfSegmentValues) * 100) : segment.numberValue;
          const percentageString = percentageNumber < 0.1 && percentageNumber > 0 ? "< 0.1%"
            : percentageNumber > 99.9 && percentageNumber < 100 ? "> 99.9%"
              : `${percentageNumber.toFixed(1)}%`;
          if (sumOfSegmentValues === 0 || percentageNumber === 0) {
            return null;
          }
          // color format #RRGGBBAA where AA is opacity/alpha. 33 is 0.20 opacity. These appear to be converted to rgba on rendering.
          const backgroundColor = props.isFaded ? `${segment.backgroundColor}33` : segment.backgroundColor;
          return (
            <TooltipWrapper
              key={segment.label}
              text={props.showTooltips ? getTooltipText(segment, percentageString) : null}
              containerClassName="metrics-stacked-bar-lite__segment"
              style={{ width: `${Math.max(percentageNumber, MIN_SEGMENT_WIDTH)}%` }}
            >
              <Segment
                backgroundColorCalculated={backgroundColor}
                backgroundColorOriginal={segment.backgroundColor}
                isFaded={props.isFaded}
                percentageNumber={percentageNumber}
                percentageString={percentageString}
                showTooltips={props.showTooltips}
              />
            </TooltipWrapper>
          );
        })}
        <figcaption className="visually-hidden">{figCaption}</figcaption>
      </figure>
      {props.showLegend ? (
        <div className="metrics-stacked-bar-lite__legend" id={`legend:${id}`}>
          {props.segments.map((segment) => {
            return (
              <span className="metrics-stacked-bar-lite__legend-item" key={segment.label}>
                <span className="metrics-color-swatch" style={{ backgroundColor: segment.backgroundColor }} />
                <span className="metrics-stacked-bar-lite__legend-label">{segment.label} <span>{(segment.numberValue || 0).toLocaleString()}</span></span>
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

interface SegmentProps {
  backgroundColorCalculated: string;
  backgroundColorOriginal: string;
  isFaded: boolean;
  percentageNumber: number;
  percentageString: string;
  showTooltips: boolean;
}

// moved this into its own function to use hooks more easily
const Segment: FC<SegmentProps> = (props: SegmentProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isOverflow = useIsOverflow(ref);
  // if we actually remove the text, isOverflow freaks out because the span width changes
  // so we'll just make it invisible while maintaining the size
  const spanStyle: CSSProperties = isOverflow
    ? { color: "transparent", padding: "0 0.25rem", userSelect: "none" }
    : { color: props.isFaded ? QesColors.QesPrimaryGray8 : backgroundColorToTextColorMap.get(props.backgroundColorOriginal) || QesColors.QesPrimaryGray8, padding: "0 0.25rem" };

  return (
    <div
      className="metrics-stacked-bar-lite__segment"
      style={{ backgroundColor: props.backgroundColorCalculated, width: props.showTooltips ? "100%" : `${Math.max(MIN_SEGMENT_WIDTH, props.percentageNumber)}%` }}
    >
      <span
        ref={ref}
        style={spanStyle}>{props.percentageString}</span>
    </div>
  )
}
